/**
 * OneTrust consent management utilities
 */

import { getCookie } from './cookie-utils'
import type { OneTrustConsentPreferences } from './types'
import { OneTrustCategories } from './types'

/**
 * Default OneTrust consent string
 */
export const OPTANON_DEFAULT_CONSENT = 'C0001:1,C0003:0,C0004:0,C0002:0'

/**
 * OneTrust consent cookie name
 */
export const OPTANON_COOKIE_NAME = 'OptanonConsent'

/**
 * Gets the OneTrust consent string from cookies
 * @returns The consent string or default consent if not found
 */
export const getOneTrustConsent = (): string => {
  let consentOnetrust = OPTANON_DEFAULT_CONSENT
  let cookieOptanonConsent = getCookie(OPTANON_COOKIE_NAME)

  if (cookieOptanonConsent) {
    cookieOptanonConsent = decodeURIComponent(cookieOptanonConsent)

    if (cookieOptanonConsent.indexOf('&groups=') !== -1) {
      consentOnetrust = cookieOptanonConsent.split('&groups=')[1].split('&')[0]
    }
  }

  return consentOnetrust
}

/**
 * Transforms a OneTrust consent string into a structured object
 * @param consent - The consent string to transform
 * @returns Object containing category preferences
 */
export const transformOneTrustConsent = (
  consent: string,
): OneTrustConsentPreferences => {
  const categories = consent
    .split(',')
    .reduce((acc: Record<string, boolean>, category: string) => {
      const [key, value] = category.split(':')
      acc[key] = value === '1'
      return acc
    }, {})

  return {
    categoryPreferences: categories,
  }
}

/**
 * Gets and transforms OneTrust consent in one call
 * @returns Object containing category preferences from current consent
 */
export const getOneTrustConsentPreferences = (): Record<string, boolean> => {
  const consent = getOneTrustConsent()
  const { categoryPreferences } = transformOneTrustConsent(consent)
  return categoryPreferences
}

/**
 * Checks if a specific consent category is enabled
 * @param category - The category to check (e.g., 'C0001', 'C0002')
 * @returns True if the category is enabled, false otherwise
 */
export const isConsentCategoryEnabled = (category: string): boolean => {
  const preferences = getOneTrustConsentPreferences()
  return preferences[category] === true
}

// Re-export the categories enum for convenience
export { OneTrustCategories }
